import { PlusCircleIcon, QuestionCircleIcon } from '@mm/icons';
import { useTriggerCRUDActions } from '@mm/state/actions/TriggerCRUDActions';
import TriggerModel from '@mm/state/model/TriggerModel';
import { qAndATriggersSelector } from '@mm/state/selectors/triggerSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { filterSuggestions } from '../../common/suggestions';
import { TriggerCatchAll } from '../TriggerCatchAll/TriggerCatchAll';
import { TriggerItem } from '../TriggerItem/TriggerItem';
import './TriggerGrid.scss';
import { PrimaryToolTip } from '../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import { KeywordsProTips } from '../../../../components/KeywordsProTips/KeywordsProTips';

type Props = {
  query: string;
  createTrigger: () => void;
};

export const TriggerGrid = ({ query, createTrigger }: Props) => {
  const { update: updateTrigger, delete: deleteTrigger } =
    useTriggerCRUDActions();

  const pages = useSelector((state: StoreState) => state.page.dropdown);
  const pageGroups = useSelector(
    (state: StoreState) => state.pageGroup.entities,
  );
  const triggers = useSelector(qAndATriggersSelector);
  const attributes = useSelector(
    (state: StoreState) => state.attribute.entities,
  );
  const suggestions = useSelector(
    (state: StoreState) => state.suggestion.entities,
  );

  const activeChatbotId = useSelector((state: StoreState) =>
    activeChatbotIdSelector(state),
  );

  const [alertVisible, setAlertVisible] = useState(true);

  const onDeleteItem = (entity: TriggerModel) => () => {
    deleteTrigger(entity);
  };

  const triggerScrollRef = useRef<HTMLDivElement>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const scrollTillTopTrigger = () => {
    if (triggerScrollRef.current) {
      const { children } = triggerScrollRef.current;

      if (children.length) {
        const firstChild = children[0];
        firstChild.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
        firstChild.querySelector('input')?.focus();
      }
    }
  };

  useEffect(() => {
    if (isInitialLoad) {
      setIsInitialLoad(false);
    } else {
      scrollTillTopTrigger();
    }
  }, [isInitialLoad, triggers.length]);

  const getExistingPhrases = () =>
    triggers
      .map(trigger => trigger.phrases.map(phrase => phrase.phrase))
      .reduce((acc, phrases) => [...acc, ...phrases], []);

  const renderRows = () => {
    const filteredSuggestions = filterSuggestions(triggers, suggestions);
    let triggerItems = [...triggers];
    if (query) {
      triggerItems = triggerItems.filter(trigger =>
        trigger.phrases.some(p =>
          p.phrase.toLowerCase().includes(query.toLowerCase()),
        ),
      );
    }

    return triggerItems
      .sort((a, b) => b.sortOrder - a.sortOrder)
      .map(trigger => (
        <div
          key={trigger.id + trigger.sortOrder}
          className="trigger-grid__item-container"
        >
          <TriggerItem
            pages={pages}
            pageGroups={pageGroups}
            trigger={trigger}
            suggestions={filteredSuggestions}
            existingPhrases={getExistingPhrases()}
            attributes={attributes}
            onUpdate={updateTrigger}
            onDelete={onDeleteItem(trigger)}
            activeChatbotId={activeChatbotId}
          />
        </div>
      ));
  };

  const renderToolTip = (
    replyTerm: string,
    ph1?: string,
    ph2?: string,
    ph3?: string,
    ph4?: string,
  ) => {
    return (
      <PrimaryToolTip
        classNameTitle="live-chat-conversation__instagram-note-tooltip-title"
        classNameContent="live-chat-conversation__instagram-note-tooltip"
        classNameContainer="live-chat-conversation__instagram-note-icon-tooltip"
        classNameArrow="live-chat-conversation__instagram-note-tooltip-arrow"
        classNamePopoverContainer="live-chat-conversation__instagram-note-tooltip-popover"
        position="right"
        withoutLine
        useHover
        anchorElement={
          <QuestionCircleIcon
            className="live-chat--conversation__instagram-info"
            color="blue"
          />
        }
        title="Example"
      >
        In conversation with the bot user replies: <strong>{replyTerm}</strong>
        <ul
          style={{ marginTop: '10px' }}
          className="live-chat-conversation__instagram-note-tooltip-list"
        >
          <strong>Matching Q&A Keywords</strong>
          {ph1 && (
            <li className="live-chat-conversation__instagram-note-tooltip-list-item">
              {ph1}
            </li>
          )}
          {ph2 && (
            <li className="live-chat-conversation__instagram-note-tooltip-list-item">
              {ph2}
            </li>
          )}
        </ul>
        <ul className="live-chat-conversation__instagram-note-tooltip-list">
          <strong>Not Matching Q&A Keyword</strong>
          {ph3 && (
            <li className="live-chat-conversation__instagram-note-tooltip-list-item">
              {ph3}
            </li>
          )}
          {ph4 && (
            <li className="live-chat-conversation__instagram-note-tooltip-list-item">
              {ph4}
            </li>
          )}
        </ul>
      </PrimaryToolTip>
    );
  };

  return (
    <div className="trigger-grid">
      <div className="trigger-grid__catch-all">
        <TriggerCatchAll />
      </div>
      {alertVisible && <KeywordsProTips setAlertVisible={setAlertVisible} />}
      <div ref={triggerScrollRef} className="trigger-grid__list-container">
        {renderRows()}
        {triggers.length > 1 && (
          <PrimaryButton
            className="trigger-grid__create-qa-button"
            onClick={createTrigger}
            leftIcon={<PlusCircleIcon />}
          >
            Create New
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};
